import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as pdfjs from 'pdfjs-dist';
import { ModalShell, ModalHeader } from 'components/ui/ModalFrame';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { MODAL_LAYER } from 'constants/modalLayers';
import { cn } from 'utils/cn';
import contractService from 'services/contractService';
import {
  fetchSignatureFields,
  saveSignatureFields,
} from 'services/signatureFieldsService';
import {
  sendDocumentForSignature,
  validateSignatureJob,
} from 'services/signatureService';
import {
  downloadUploadedSignaturePdf,
  isUploadedSignatureDocument,
} from 'services/uploadedDocumentsService';
import { screenRectToPdfAnchor } from 'utils/signatureFieldCoordinates';
import userService from 'services/userService';
import {
  createSignatureField,
  formatCpfMask,
  formatTelefoneMask,
  hasUnsavedDraft,
  mergeSignersAndAnchors,
  NOTIFICATION_CHANNELS,
  splitFieldsToSavePayload,
  validateSignatureFields,
} from 'utils/signatureFieldModel';
import PdfPageViewer from './PdfPageViewer';
import SignatureFieldsSidebar from './SignatureFieldsSidebar';
import SignatureFieldFormPanel from './SignatureFieldFormPanel';
import SignatureSendConfirmDialog from '../SignatureSendConfirmDialog';
import SpeedDialFab from 'components/ui/SpeedDialFab';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const MOBILE_VIEWPORT_MQ = '(max-width: 1023px)';
const MOBILE_FORM_PEEK_PX = 64;
const MOBILE_FORM_EXPANDED_MAX = 'min(46dvh, 22rem)';

function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(MOBILE_VIEWPORT_MQ).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_VIEWPORT_MQ);
    const onChange = (event) => setIsMobile(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

const EDITABLE_STATUSES = new Set(['PDF_GENERATED', 'PENDING_SIGNATURE', 'PDF_UPLOADED']);

function normalizeStatus(raw) {
  if (raw == null) return '';
  const u = String(raw).trim().toUpperCase();
  if (u === 'AWAITING_SIGNATURE') return 'PENDING_SIGNATURE';
  if (u === 'SIGNED' || u === 'SIGN' || u === 'ASSINADO') return 'SIGNED';
  return u;
}

async function loadSignatureFieldsForEditor(jobId, documentFallback) {
  const fieldsResult = await fetchSignatureFields(jobId);
  if (fieldsResult.ok) {
    return fieldsResult.data;
  }

  return {
    manual_signers: documentFallback?.manual_signers,
    signature_anchors: documentFallback?.signature_anchors,
  };
}

const SignatureFieldEditorModal = ({
  open,
  jobId,
  documentTitle: documentTitleProp = '',
  documentStatus: documentStatusProp = '',
  onClose,
  onSentToSignature,
}) => {
  const [documentMeta, setDocumentMeta] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [fields, setFields] = useState([]);
  const [selectedSignerId, setSelectedSignerId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageDimensions, setPageDimensions] = useState({});
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [validatingSend, setValidatingSend] = useState(false);
  const [signingSend, setSigningSend] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [editingSignerId, setEditingSignerId] = useState(null);
  const [scrollToFieldTrigger, setScrollToFieldTrigger] = useState(0);
  const [addingMySignature, setAddingMySignature] = useState(false);
  const [mobileFormExpanded, setMobileFormExpanded] = useState(false);
  const [mobileFieldsOpen, setMobileFieldsOpen] = useState(false);
  const initialFieldsRef = useRef('[]');
  const prevFormFieldIdRef = useRef(null);
  const isMobile = useIsMobileViewport();

  const documentTitle =
    documentMeta?.title ||
    documentMeta?.name ||
    documentMeta?.document_title ||
    documentTitleProp ||
    'Documento';

  const documentStatus = normalizeStatus(documentMeta?.status ?? documentStatusProp);

  const selectedField = useMemo(
    () => fields.find((f) => f.signer_id === selectedSignerId) || null,
    [fields, selectedSignerId]
  );

  const formField = useMemo(() => {
    if (editingSignerId) {
      return fields.find((f) => f.signer_id === editingSignerId) || null;
    }
    if (selectedField?.isDraft && !selectedField?.isConfirmed) {
      return selectedField;
    }
    return null;
  }, [editingSignerId, fields, selectedField]);

  useEffect(() => {
    if (!isMobile) return;
    const nextId = formField?.signer_id || null;
    if (nextId && nextId !== prevFormFieldIdRef.current) {
      // New signer form: peek first so the PDF stays usable for positioning.
      setMobileFormExpanded(false);
      setMobileFieldsOpen(false);
    }
    if (!nextId) {
      setMobileFormExpanded(false);
    }
    prevFormFieldIdRef.current = nextId;
  }, [formField?.signer_id, isMobile]);

  const canAddField = !hasUnsavedDraft(fields);
  const confirmedFieldCount = fields.filter((f) => f.isConfirmed).length;
  const canSendToSignature = documentStatus !== 'PENDING_SIGNATURE';
  const busy = saving || validatingSend || signingSend || addingMySignature;

  const persistFields = useCallback(
    async (nextFields, { successMessage } = {}) => {
      if (!jobId) return false;

      const validation = validateSignatureFields(nextFields);
      if (!validation.ok) {
        toast.error(validation.message);
        return false;
      }

      setSaving(true);
      try {
        const payload = splitFieldsToSavePayload(nextFields);
        const result = await saveSignatureFields(jobId, payload);
        if (!result.ok) {
          toast.error(result.message || 'Não foi possível salvar os campos.');
          return false;
        }

        initialFieldsRef.current = JSON.stringify(nextFields);
        setDirty(false);
        if (successMessage) toast.success(successMessage);
        return true;
      } catch (err) {
        console.error('persistFields:', err);
        toast.error('Erro ao salvar os campos de assinatura.');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [jobId]
  );

  const resetState = useCallback(() => {
    setDocumentMeta(null);
    setPdfBlob(null);
    setFields([]);
    setSelectedSignerId(null);
    setCurrentPage(1);
    setTotalPages(0);
    setPageDimensions({});
    setLoadError(null);
    setDirty(false);
    setEditingSignerId(null);
    setMobileFormExpanded(false);
    setMobileFieldsOpen(false);
    prevFormFieldIdRef.current = null;
    initialFieldsRef.current = '[]';
  }, []);

  const handlePageDimensions = useCallback((pageNumber, widthPt, heightPt) => {
    setPageDimensions((prev) => ({
      ...prev,
      [pageNumber]: { widthPt, heightPt },
    }));
  }, []);

  useEffect(() => {
    if (!open || !jobId) {
      resetState();
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoadingMeta(true);
      setLoadError(null);

      try {
        const metaResult = await contractService.getDocumentByJobId(jobId);
        if (cancelled) return;

        if (!metaResult?.success) {
          setLoadError('Não foi possível carregar os dados do documento.');
          return;
        }

        const data = metaResult.data || {};
        const status = normalizeStatus(data.status);

        if (!EDITABLE_STATUSES.has(status) && status !== 'SIGNED') {
          setLoadError('Só é possível editar campos quando o PDF já foi gerado.');
          setDocumentMeta(data);
          return;
        }

        if (!data.s3_key) {
          setLoadError('Documento sem PDF disponível (s3_key ausente).');
          setDocumentMeta(data);
          return;
        }

        setDocumentMeta(data);

        const signatureData = await loadSignatureFieldsForEditor(jobId, data);
        if (cancelled) return;

        const merged = mergeSignersAndAnchors(
          signatureData.manual_signers,
          signatureData.signature_anchors
        );
        setFields(merged);
        initialFieldsRef.current = JSON.stringify(merged);

        if (merged.length > 0) {
          setSelectedSignerId(merged[0].signer_id);
          setCurrentPage(merged[0].page_number || 1);
        }

        setLoadingPdf(true);
        const downloadResult = isUploadedSignatureDocument(data)
          ? await (async () => {
              const r = await downloadUploadedSignaturePdf(jobId);
              if (!r.success) return { success: false };
              return {
                success: true,
                data: {
                  blob: r.data.blob,
                  fileName: r.data.fileName,
                },
              };
            })()
          : await contractService.downloadDocument({
              s3Key: data.s3_key,
              name: data.title || documentTitleProp || 'documento',
            });
        if (cancelled) return;

        if (!downloadResult?.success) {
          setLoadError('Não foi possível carregar o PDF do documento.');
          return;
        }

        const blob = downloadResult.data?.blob;
        setPdfBlob(blob);

        const arrayBuffer = await blob.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;
        setTotalPages(pdf.numPages);
      } catch (err) {
        console.error('SignatureFieldEditorModal load:', err);
        if (!cancelled) {
          setLoadError('Erro ao carregar o editor de assinatura.');
        }
      } finally {
        if (!cancelled) {
          setLoadingMeta(false);
          setLoadingPdf(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [documentTitleProp, jobId, open, resetState]);

  const handleClose = useCallback(() => {
    const hasDraft = hasUnsavedDraft(fields);
    const isDirty =
      dirty || JSON.stringify(fields) !== initialFieldsRef.current || hasDraft;

    if (isDirty) {
      const confirmed = window.confirm(
        'Existem alterações não salvas. Deseja sair sem salvar?'
      );
      if (!confirmed) return;
    }
    resetState();
    onClose?.();
  }, [dirty, fields, onClose, resetState]);

  const handleAddField = useCallback(() => {
    const dims = pageDimensions[currentPage];
    const widthPt = dims?.widthPt || 595;
    const heightPt = dims?.heightPt || 842;

    const field = createSignatureField(currentPage, widthPt, heightPt);
    setFields((prev) => [...prev, field]);
    setSelectedSignerId(field.signer_id);
    setEditingSignerId(null);
    setScrollToFieldTrigger((n) => n + 1);
    setDirty(true);
  }, [currentPage, pageDimensions]);

  const handleAddMySignature = useCallback(async () => {
    if (addingMySignature) return;

    const dims = pageDimensions[currentPage];
    const widthPt = dims?.widthPt || 595;
    const heightPt = dims?.heightPt || 842;

    setAddingMySignature(true);
    try {
      const [userResult, configResponse] = await Promise.all([
        userService.getUserInfo(),
        userService.loadUserConfigurations(),
      ]);

      const config = configResponse?.configuration ?? {};
      const email =
        userResult?.success && userResult.data?.email
          ? String(userResult.data.email).trim()
          : '';
      const fullName = String(config.full_name ?? '').trim();
      const cpfRaw = config.cpf ?? '';
      const cellphoneRaw = config.cellphone ?? '';

      const field = {
        ...createSignatureField(currentPage, widthPt, heightPt),
        full_name: fullName,
        email,
        cpf: cpfRaw ? formatCpfMask(cpfRaw) : '',
        telefone: cellphoneRaw ? formatTelefoneMask(cellphoneRaw) : '',
      };

      setFields((prev) => [...prev, field]);
      setSelectedSignerId(field.signer_id);
      setEditingSignerId(null);
      setScrollToFieldTrigger((n) => n + 1);
      setDirty(true);
    } catch (err) {
      console.error('handleAddMySignature:', err);
      toast.error('Não foi possível carregar seus dados pessoais.');
    } finally {
      setAddingMySignature(false);
    }
  }, [addingMySignature, currentPage, pageDimensions]);

  const handleUpdateFieldGeometry = useCallback(
    (signerId, screenRect, canvasRect, dims) => {
      const anchor = screenRectToPdfAnchor(
        screenRect,
        canvasRect,
        dims.widthPt,
        dims.heightPt,
        currentPage,
        signerId
      );

      setFields((prev) =>
        prev.map((f) =>
          f.signer_id === signerId
            ? {
                ...f,
                ...anchor,
                page_number: currentPage,
              }
            : f
        )
      );
      setDirty(true);
    },
    [currentPage]
  );

  const handleFormChange = useCallback((key, value) => {
    const targetId = editingSignerId || selectedSignerId;
    if (!targetId) return;

    setFields((prev) =>
      prev.map((f) => (f.signer_id === targetId ? { ...f, [key]: value } : f))
    );
    setDirty(true);
  }, [editingSignerId, selectedSignerId]);

  const handleConfirmField = useCallback(() => {
    const targetId = editingSignerId || selectedSignerId;
    if (!targetId) return;

    setFields((prev) =>
      prev.map((f) =>
        f.signer_id === targetId
          ? { ...f, isDraft: false, isConfirmed: true }
          : f
      )
    );
    setEditingSignerId(null);
    setDirty(true);
  }, [editingSignerId, selectedSignerId]);

  const handleDiscardDraft = useCallback(() => {
    const targetId = editingSignerId || selectedSignerId;
    if (!targetId) return;

    setFields((prev) => {
      const field = prev.find((f) => f.signer_id === targetId);
      if (field?.isConfirmed) return prev;
      return prev.filter((f) => f.signer_id !== targetId);
    });
    setSelectedSignerId(null);
    setEditingSignerId(null);
    setDirty(true);
  }, [editingSignerId, selectedSignerId]);

  const handleRemoveField = useCallback(
    async (signerId) => {
      const field = fields.find((f) => f.signer_id === signerId);
      const label = field?.full_name || 'este campo';
      if (!window.confirm(`Remover o campo de assinatura de ${label}?`)) return;

      const previousFields = fields;
      const nextFields = previousFields.filter((f) => f.signer_id !== signerId);

      setFields(nextFields);
      if (selectedSignerId === signerId) setSelectedSignerId(null);
      if (editingSignerId === signerId) setEditingSignerId(null);

      if (!field?.isConfirmed) {
        setDirty(true);
        return;
      }

      const ok = await persistFields(nextFields, {
        successMessage: 'Campo de assinatura removido.',
      });
      if (!ok) {
        setFields(previousFields);
      }
    },
    [editingSignerId, fields, persistFields, selectedSignerId]
  );

  const handleSelectField = useCallback((signerId) => {
    setSelectedSignerId(signerId);
    setEditingSignerId(null);
    setScrollToFieldTrigger((n) => n + 1);
    const field = fields.find((f) => f.signer_id === signerId);
    if (field?.page_number) {
      setCurrentPage(field.page_number);
    }
  }, [fields]);

  const handleEditField = useCallback((signerId) => {
    setSelectedSignerId(signerId);
    setEditingSignerId(signerId);
    setScrollToFieldTrigger((n) => n + 1);
    const field = fields.find((f) => f.signer_id === signerId);
    if (field?.page_number) {
      setCurrentPage(field.page_number);
    }
  }, [fields]);

  const handleSave = useCallback(async () => {
    await persistFields(fields, { successMessage: 'Campos de assinatura salvos' });
  }, [fields, persistFields]);

  const handleRequestSendToSignature = useCallback(async () => {
    if (hasUnsavedDraft(fields)) {
      toast.error('Confirme ou remova o campo de assinatura em edição.');
      return;
    }

    const validation = validateSignatureFields(fields, { forSend: true });
    if (!validation.ok) {
      toast.error(validation.message);
      return;
    }

    const needsSave =
      dirty || JSON.stringify(fields) !== initialFieldsRef.current;
    if (needsSave) {
      const saved = await persistFields(fields);
      if (!saved) return;
    }

    setValidatingSend(true);
    try {
      const result = await validateSignatureJob(jobId);
      if (!result.ok) {
        toast.error(result.messagePt);
        return;
      }
    } catch (err) {
      console.error('handleRequestSendToSignature validate:', err);
      toast.error('Erro ao validar documento antes de enviar.');
      return;
    } finally {
      setValidatingSend(false);
    }

    setSendConfirmOpen(true);
  }, [dirty, fields, jobId, persistFields]);

  const handleConfirmSendToSignature = useCallback(async (notificationChannel) => {
    const channel = notificationChannel || NOTIFICATION_CHANNELS.EMAIL;
    const channelValidation = validateSignatureFields(fields, {
      forSend: true,
      requireEmail: channel === NOTIFICATION_CHANNELS.EMAIL,
      requireTelefone: channel === NOTIFICATION_CHANNELS.WHATSAPP,
    });
    if (!channelValidation.ok) {
      toast.error(channelValidation.message);
      return;
    }

    setSendConfirmOpen(false);

    setSigningSend(true);
    try {
      const result = await sendDocumentForSignature(
        jobId,
        documentMeta,
        channel
      );

      if (!result.ok) {
        toast.error(result.messagePt);
        return;
      }

      resetState();
      onSentToSignature?.();
    } catch (err) {
      console.error('handleConfirmSendToSignature:', err);
      toast.error('Erro ao enviar documento para assinatura.');
    } finally {
      setSigningSend(false);
    }
  }, [documentMeta, fields, jobId, onSentToSignature, resetState]);

  const mobileSpeedDialActions = useMemo(() => {
    if (!isMobile) return [];
    const loading = loadingMeta || loadingPdf;
    return [
      {
        id: 'fields',
        label: `Campos (${confirmedFieldCount})`,
        iconName: 'List',
        disabled: busy,
        onClick: () => {
          setMobileFieldsOpen(true);
          setMobileFormExpanded(false);
        },
      },
      {
        id: 'my-signature',
        label: addingMySignature ? 'Adicionando…' : 'Minha assinatura',
        iconName: 'UserPlus',
        disabled: !canAddField || loading || addingMySignature || busy,
        onClick: () => {
          void handleAddMySignature();
        },
      },
      {
        id: 'add-field',
        label: 'Novo campo',
        iconName: 'Plus',
        disabled: !canAddField || loading || addingMySignature || busy,
        onClick: () => {
          handleAddField();
        },
      },
      {
        id: 'save',
        label: saving ? 'Salvando…' : 'Salvar campos',
        iconName: 'Save',
        variant: 'primary',
        disabled:
          loading ||
          Boolean(loadError) ||
          hasUnsavedDraft(fields) ||
          validatingSend ||
          signingSend,
        onClick: () => {
          void handleSave();
        },
      },
      canSendToSignature
        ? {
            id: 'send',
            label:
              validatingSend || signingSend
                ? 'Enviando…'
                : 'Enviar para assinatura',
            iconName: 'Send',
            variant: 'primary',
            disabled:
              loading ||
              Boolean(loadError) ||
              busy ||
              hasUnsavedDraft(fields) ||
              confirmedFieldCount === 0,
            onClick: () => {
              void handleRequestSendToSignature();
            },
          }
        : null,
    ].filter(Boolean);
  }, [
    addingMySignature,
    busy,
    canAddField,
    canSendToSignature,
    confirmedFieldCount,
    fields,
    handleAddField,
    handleAddMySignature,
    handleRequestSendToSignature,
    handleSave,
    isMobile,
    loadError,
    loadingMeta,
    loadingPdf,
    saving,
    signingSend,
    validatingSend,
  ]);

  if (!open) return null;

  const isLoading = loadingMeta || loadingPdf;
  const mobileSheetExpanded = Boolean(formField) && mobileFormExpanded;
  const mobilePdfPad = formField
    ? mobileSheetExpanded
      ? MOBILE_FORM_EXPANDED_MAX
      : `${MOBILE_FORM_PEEK_PX}px`
    : mobileFieldsOpen
      ? MOBILE_FORM_EXPANDED_MAX
      : '5.5rem';

  return (
    <ModalShell
      zClass={MODAL_LAYER.purchase}
      backdropClassName="bg-black/60"
      backdropPadding={isMobile ? 'none' : 'all'}
      panelClassName={cn(
        'relative bg-card shadow-elevation-lg flex flex-col overflow-hidden',
        isMobile
          ? 'rounded-none w-full max-w-none h-[100dvh] max-h-[100dvh]'
          : 'rounded-xl w-full max-w-[calc(100vw-1.5rem)] xl:max-w-[min(1400px,calc(100vw-2rem))] h-[calc(100dvh-1.5rem)] max-h-[calc(100dvh-1.5rem)]'
      )}
    >
      <ModalHeader
        title={
          isMobile
            ? `Assinaturas — ${documentTitle}`
            : `Campos de assinatura — ${documentTitle}`
        }
        onClose={handleClose}
        className="px-3 py-2 lg:px-4 lg:py-4 md:px-5"
        titleClassName="text-sm lg:text-lg truncate pr-2"
      />

      {/* Desktop actions */}
      <div className="hidden lg:flex px-4 md:px-5 py-2 border-b border-border flex-shrink-0 overflow-x-auto">
        <div className="flex items-center justify-end gap-2 min-w-max sm:min-w-0 sm:flex-wrap ml-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="lg:h-10 lg:px-4 lg:text-sm"
            onClick={handleClose}
            disabled={busy}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            className="lg:h-10 lg:px-4 lg:text-sm"
            onClick={handleSave}
            loading={saving}
            disabled={
              isLoading ||
              Boolean(loadError) ||
              hasUnsavedDraft(fields) ||
              validatingSend ||
              signingSend
            }
            iconName="Save"
          >
            Salvar campos
          </Button>
          {canSendToSignature ? (
            <Button
              type="button"
              size="sm"
              className="lg:h-10 lg:px-4 lg:text-sm"
              onClick={handleRequestSendToSignature}
              loading={validatingSend || signingSend}
              disabled={
                isLoading ||
                Boolean(loadError) ||
                busy ||
                hasUnsavedDraft(fields) ||
                confirmedFieldCount === 0
              }
              iconName="Send"
            >
              Enviar para assinatura
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden relative">
        <div
          className="flex-1 min-w-0 min-h-0 border-b lg:border-b-0 lg:border-r border-border"
          style={isMobile ? { paddingBottom: mobilePdfPad } : undefined}
        >
          <PdfPageViewer
            open={open}
            pdfBlob={pdfBlob}
            fields={fields}
            selectedSignerId={selectedSignerId}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onSelectField={handleSelectField}
            onUpdateFieldGeometry={handleUpdateFieldGeometry}
            onAddField={handleAddField}
            onAddMySignature={handleAddMySignature}
            addingMySignature={addingMySignature}
            scrollToFieldTrigger={scrollToFieldTrigger}
            loadingPdf={isLoading}
            loadError={loadError}
            canAddField={canAddField}
            pageDimensions={pageDimensions}
            onPageDimensions={handlePageDimensions}
          />
        </div>

        <div className="hidden lg:flex w-full lg:w-[320px] xl:w-[360px] flex-shrink-0 flex-col min-h-0 max-h-none">
          <div className="flex-1 min-h-0 overflow-hidden">
            <SignatureFieldsSidebar
              fields={fields}
              selectedSignerId={selectedSignerId}
              onSelect={handleSelectField}
              onEdit={handleEditField}
              onRemove={handleRemoveField}
              disabled={busy}
            />
          </div>

          {formField ? (
            <div className="border-t border-border p-3 flex-shrink-0 max-h-[50vh] overflow-y-auto">
              <SignatureFieldFormPanel
                field={formField}
                onChange={handleFormChange}
                onConfirm={handleConfirmField}
                onCancel={handleDiscardDraft}
              />
            </div>
          ) : null}
        </div>

        {/* Mobile: fields list sheet */}
        {isMobile && mobileFieldsOpen && !formField ? (
          <div
            className="absolute inset-x-0 bottom-0 z-20 flex flex-col border-t border-border bg-card shadow-[0_-8px_24px_rgba(0,0,0,0.12)] rounded-t-xl pb-[env(safe-area-inset-bottom,0px)]"
            style={{ maxHeight: MOBILE_FORM_EXPANDED_MAX, height: MOBILE_FORM_EXPANDED_MAX }}
          >
            <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30">
              <p className="text-sm font-medium text-foreground">Campos ({confirmedFieldCount})</p>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="h-8 w-8 px-0"
                iconName="ChevronDown"
                onClick={() => setMobileFieldsOpen(false)}
                aria-label="Fechar lista de campos"
              />
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <SignatureFieldsSidebar
                fields={fields}
                selectedSignerId={selectedSignerId}
                onSelect={(id) => {
                  handleSelectField(id);
                  setMobileFieldsOpen(false);
                }}
                onEdit={(id) => {
                  handleEditField(id);
                  setMobileFieldsOpen(false);
                  setMobileFormExpanded(true);
                }}
                onRemove={handleRemoveField}
                disabled={busy}
              />
            </div>
          </div>
        ) : null}

        {/* Mobile: collapsible signer form sheet */}
        {isMobile && formField ? (
          <div
            className="absolute inset-x-0 bottom-0 z-20 flex flex-col border-t border-border bg-card shadow-[0_-8px_24px_rgba(0,0,0,0.12)] rounded-t-xl pb-[env(safe-area-inset-bottom,0px)] transition-[height,max-height] duration-200 ease-out"
            style={
              mobileSheetExpanded
                ? { height: MOBILE_FORM_EXPANDED_MAX, maxHeight: MOBILE_FORM_EXPANDED_MAX }
                : { height: MOBILE_FORM_PEEK_PX, maxHeight: MOBILE_FORM_PEEK_PX }
            }
          >
            <button
              type="button"
              className="flex-shrink-0 w-full flex flex-col items-stretch gap-1 px-3 pt-1.5 pb-2 border-b border-border bg-muted/30 text-left"
              onClick={() => setMobileFormExpanded((v) => !v)}
              aria-expanded={mobileSheetExpanded}
              aria-label={
                mobileSheetExpanded
                  ? 'Recolher formulário do signatário'
                  : 'Expandir formulário do signatário'
              }
            >
              <span className="mx-auto w-10 h-1 rounded-full bg-border shrink-0" aria-hidden />
              <span className="flex items-center gap-2 min-w-0">
                <span className="flex-1 min-w-0 text-xs font-medium text-muted-foreground truncate">
                  {mobileSheetExpanded
                    ? 'Posicione no PDF e preencha os dados'
                    : 'Toque para preencher dados do signatário'}
                </span>
                <Icon
                  name={mobileSheetExpanded ? 'ChevronDown' : 'ChevronUp'}
                  size={18}
                  className="text-muted-foreground shrink-0"
                />
              </span>
            </button>
            {mobileSheetExpanded ? (
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <SignatureFieldFormPanel
                  field={formField}
                  onChange={handleFormChange}
                  onConfirm={handleConfirmField}
                  onCancel={handleDiscardDraft}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <SignatureSendConfirmDialog
        open={sendConfirmOpen}
        zClass={MODAL_LAYER.payment}
        onCancel={() => setSendConfirmOpen(false)}
        onConfirm={handleConfirmSendToSignature}
      />

      {isMobile ? (
        <SpeedDialFab
          ariaLabel="Ações de assinatura"
          visibilityClassName="lg:hidden"
          className={cn(
            'z-[4100]',
            formField || mobileFieldsOpen
              ? mobileSheetExpanded || mobileFieldsOpen
                ? 'bottom-[calc(min(46dvh,22rem)+1rem+env(safe-area-inset-bottom,0px))]'
                : 'bottom-[calc(5rem+env(safe-area-inset-bottom,0px))]'
              : null
          )}
          actions={mobileSpeedDialActions}
        />
      ) : null}
    </ModalShell>
  );
};

export default SignatureFieldEditorModal;
